import { createGtm, useGtm } from '@gtm-support/vue-gtm'
import type {
    HblCart,
    HblLineItem,
    HblOrder,
    HblOrderLineItem,
    HblPaymentMethod,
    HblProduct,
    HblShippingMethod
} from '@/utils/types'
import { useCustomer , useRuntimeConfig } from '#imports'
import { storeToRefs } from 'pinia'

function clearEcommerce () {
    const dataLayer = window.dataLayer = window.dataLayer ?? [];
    dataLayer.push({ ecommerce: null });
}

function pushEvent(event) {
    const dataLayer = window.dataLayer = window.dataLayer ?? [];
    dataLayer.push(event);
}

/** Unit discount against the strike-through list price. */
function calculateDiscount (listPrice: string | number | undefined, unitPrice: number): number {
    const discount = roundPrice(Number(listPrice) - unitPrice)

    return discount > 0 ? discount : 0
}

/** Strike-through list price: order lines carry `listPrice`, cart lines and products `price.specialPrice` (Shopware's `listPrice`). */
function listPrice (product: HblProduct | HblLineItem | HblOrderLineItem): string | number | undefined {
    if ('listPrice' in product && product.listPrice != null) {
        return product.listPrice
    }

    return (product.price as { specialPrice?: string | number } | undefined)?.specialPrice
}

function roundPrice (value: number): number {
    return Math.round(value * 100) / 100
}

/** Gross unit price: order lines carry `unitPrice`, cart lines and products `price.regularPrice`. */
function unitPrice (product: HblProduct | HblLineItem | HblOrderLineItem): number {
    if ('unitPrice' in product && product.unitPrice != null) {
        return Number(product.unitPrice)
    }

    const price: unknown = product.price
    if (typeof price === 'number') {
        return price
    }

    const regularPrice = (price as { regularPrice?: string | number } | undefined)?.regularPrice

    return regularPrice != null ? Number(regularPrice) : 0
}

const NON_PRODUCT_LINE_TYPES = ['promotion', 'credit']

/** Promotion discount per promoted line from Shopware's `payload.composition`, keyed by cart line id (= order line `identifier`). */
function promotionDiscounts (lineItems: (HblLineItem | HblOrderLineItem)[]): Map<string, number> {
    const discounts = new Map<string, number>()

    lineItems
        .filter((lineItem) => lineItem.type === 'promotion')
        .flatMap((lineItem) => lineItem.payload?.composition ?? [])
        .forEach(({ id, discount }: { id: string, discount: number }) => {
            discounts.set(id, (discounts.get(id) ?? 0) + Number(discount))
        })

    return discounts
}

/**
 * Box contents (`parentId` set) are included in the box price and are sent with price 0.
 * Promoted lines carry their promotion discount per unit, which lowers `price` and adds to `discount`.
 */
function mapEcommerceItems (lineItems: (HblLineItem | HblOrderLineItem)[], affiliation: string | false) {
    const discounts = promotionDiscounts(lineItems)

    return lineItems
        .filter((lineItem) => !NON_PRODUCT_LINE_TYPES.includes(lineItem.type ?? ''))
        .map((lineItem, index) => {
            const lineDiscount = discounts.get(lineItem.identifier ?? lineItem.id) ?? 0

            return mapEcommerceItem(lineItem, affiliation, {
                index,
                qty: lineItem.quantity,
                ...(lineItem.parentId != null && { price: 0 }),
                ...(lineItem.parentId == null && lineDiscount > 0 && { promotionDiscount: roundPrice(lineDiscount / lineItem.quantity) })
            })
        })
}

function flattenCartLineItems (lineItems: HblLineItem[]): HblLineItem[] {
    return lineItems.flatMap((lineItem) => [
        lineItem,
        ...(lineItem.children ?? []).map((child: HblLineItem) => ({ ...child, parentId: lineItem.id }))
    ])
}

function sumItemsValue (items: { price: number, quantity: number }[]): number {
    return roundPrice(items.reduce((sum, item) => sum + item.price * item.quantity, 0))
}

/** Promotion codes; automatic promotions without a code fall back to their label. */
function promotionCoupon (lineItems: (HblLineItem | HblOrderLineItem)[]): string | undefined {
    const coupons = [...new Set(lineItems
        .filter((lineItem) => lineItem.type === 'promotion')
        .map((lineItem) => lineItem.payload?.code || lineItem.name)
        .filter(Boolean))]

    return coupons.length > 0 ? coupons.join(', ') : undefined
}

/** Delivery costs plus ACRIS additional costs (e.g. Verpackungspauschale), which Shopware keeps outside the deliveries. */
function orderShipping (order: HblOrder): number {
    const deliveryCosts = [order.shippingCosts ?? []].flat()
    const additionalCosts = (order.surchargeDiscount ?? []).map((surcharge) => surcharge.price?.totalPrice ?? 0)

    return roundPrice([...deliveryCosts, ...additionalCosts].reduce((sum, cost) => sum + Number(cost), 0))
}

function cartEcommerce (cart: HblCart, affiliation: string | false) {
    const items = mapEcommerceItems(flattenCartLineItems(cart.lineItems), affiliation)
    const coupon = promotionCoupon(cart.lineItems)

    return {
        value: sumItemsValue(items),
        ...(coupon != null && { coupon }),
        items
    }
}

function mapEcommerceItem (
    product: HblProduct | HblLineItem | HblOrderLineItem,
    affiliation: string | false,
    additionalData?: {
        qty?: number,
        index?: number,
        listId?: string,
        listName?: string,
        price?: number,
        promotionDiscount?: number
    }
) {
    const promotionDiscount = additionalData?.promotionDiscount ?? 0
    const discount = roundPrice(calculateDiscount(listPrice(product), unitPrice(product)) + promotionDiscount)

    // Remove "-Kopf" suffix from parent products for tracking
    const itemId = product.sku?.replace(/-Kopf$/i, '') ?? product.sku

    return {
        // @ts-ignore
        item_id: itemId, // required
        item_name: product.name, // required
        ...(affiliation && { affiliation: affiliation }),
        ...(discount > 0 && { discount: discount }),
        index: additionalData?.index != null ? additionalData?.index: 0,
        // @ts-ignore
        // ...(product?.manufacturer?.name != null && { item_brand: product?.manufacturer?.name }),
        ...(additionalData?.listId != null && { item_list_id: additionalData?.listId }),
        ...(additionalData?.listName != null && { item_list_name: additionalData?.listName }),
        price: roundPrice((additionalData?.price ?? unitPrice(product)) - promotionDiscount),
        quantity: additionalData?.qty != null ? additionalData?.qty : 1
        // item_category: 'Apparel',
        // item_category2: 'Adult',
        // item_category3: 'Shirts',
        // item_category4: 'Crew',
        // item_category5: 'Short sleeve',
        // item_variant: 'green',
        // coupon: 'SUMMER_FUN',
        // location_id: 'ChIJIQBpAG2ahYAR_6128GcTUEo',
    }
}

export default defineNuxtPlugin((nuxtApp) => {
    const runtimeConfig = useRuntimeConfig().public

    if (runtimeConfig.gtmPwaGtmEnabled != null && runtimeConfig.gtmPwaGtmEnabled === 'disabled') {
        return
    }

    // Determine GTM container ID based on sales channel ID from runtime config
    const apiSwAccessKey = runtimeConfig.apiSwAccessKey
    let gtmContainerId = null

    // Map sales channel sw access keys to GTM containers
    // German (DE): uses Shoplytics, no GTM
    // Swiss (CH): SWSCDXVLRJF2EVY0ETDZTG5XAW
    // French (FR): SWSCBNC1DVNLRLHMNDM0TTFXZQ
    if (runtimeConfig.gtmPwaGtmContainerIdFr && apiSwAccessKey === 'SWSCBNC1DVNLRLHMNDM0TTFXZQ') {
        gtmContainerId = runtimeConfig.gtmPwaGtmContainerIdFr
    } else if (runtimeConfig.gtmPwaGtmContainerIdCh && apiSwAccessKey === 'SWSCDXVLRJF2EVY0ETDZTG5XAW') {
        gtmContainerId = runtimeConfig.gtmPwaGtmContainerIdCh
    }

    let enabled = true // default
    if (runtimeConfig.gtmPwaGtmEnabled != null && runtimeConfig.gtmPwaGtmEnabled === 'enabledOnConsent') {
        enabled = false
    }

    const customerStore = useCustomer()
    const { customer } = storeToRefs(customerStore)

    const cartStore = useCart()
    const { cart } = storeToRefs(cartStore)
    const { getCart } = cartStore

    const options = {
        enabled: enabled,
        id: gtmContainerId, // Your GTM single container ID, array of container ids ['GTM-xxxxxx', 'GTM-yyyyyy'] or array of objects [{id: 'GTM-xxxxxx', queryParams: { gtm_auth: 'abc123', gtm_preview: 'env-4', gtm_cookies_win: 'x'}}, {id: 'GTM-yyyyyy', queryParams: {gtm_auth: 'abc234', gtm_preview: 'env-5', gtm_cookies_win: 'x'}}], // Your GTM single container ID or array of container ids ['GTM-xxxxxx', 'GTM-yyyyyy']
        ...((runtimeConfig.gtmPwaGtmSource != null && runtimeConfig.gtmPwaGtmSource !== '') && { source: runtimeConfig.gtmPwaGtmSource }),
        defer: runtimeConfig.gtmPwaGtmDefer != null ? runtimeConfig.gtmPwaGtmDefer : false,
        compatibility: runtimeConfig.gtmPwaGtmCompatibility != null ? runtimeConfig.gtmPwaGtmCompatibility : false,
        ...((runtimeConfig.gtmPwaGtmNonce != null && runtimeConfig.gtmPwaGtmNonce !== '') && { nonce: runtimeConfig.gtmPwaGtmNonce }),
        debug: runtimeConfig.gtmPwaGtmDebug != null ? runtimeConfig.gtmPwaGtmDebug : false,
        ...((runtimeConfig.gtmPwaGtmTrackViewEventProperty != null && runtimeConfig.gtmPwaGtmTrackViewEventProperty !== '') && { trackViewEventProperty: runtimeConfig.gtmPwaGtmTrackViewEventProperty }),
        loadScript: true,
        vueRouter: useRouter(),
        trackOnNextTick: false,
        vueRouterAdditionalEventData: (to, from) => {
            return {
                page_title: document?.getElementsByTagName('title')[0]?.innerHTML,
                user_type: customer.value != null && !customer.value.isGuest ? 'logged_in' : 'guest'
            }
        },
    }

    // For German sales channel (or any other), use Shoplytics instead of GTM
    // For other initialize GTM right here
    if (gtmContainerId && gtmContainerId !== '') {
        try {
            nuxtApp.vueApp.use(createGtm(options))
        } catch (e) {
            console.error(e)
            return
        }
    }

    const { $hblBus }: any = nuxtApp
    const currencyCode = gtmContainerId === runtimeConfig.gtmPwaGtmContainerIdCh ? runtimeConfig.gtmPwaGtmEcommerceCurrency : 'EUR'
    const affiliation = runtimeConfig.gtmPwaGtmEcommerceAffiliation != null && runtimeConfig.gtmPwaGtmEcommerceAffiliation !== '' ? runtimeConfig.gtmPwaGtmEcommerceAffiliation : false

    $hblBus.$on('addToCart', (event) => {
        const { qty, product }: { qty: number, product: HblProduct } = event

        if (product == null) {
            return
        }

        const item = mapEcommerceItem(product, affiliation, { qty })

        clearEcommerce()
        pushEvent({
            event: 'add_to_cart',
            action: 'click',
            ecommerce: {
                currency: currencyCode, // required
                value: sumItemsValue([item]), // required
                items: [item]
            }
        })
    })

    $hblBus.$on('removeFromCart', (event) => {
        const { product }: { product: HblLineItem } = event

        if (product == null) {
            return
        }

        const item = mapEcommerceItem(product, affiliation, { qty: product?.quantity })

        clearEcommerce()
        pushEvent({
            event: 'remove_from_cart',
            action: 'click',
            ecommerce: {
                currency: currencyCode, // required
                value: sumItemsValue([item]), // required
                items: [item]
            }
        })
    })

    $hblBus.$on('viewCart', (event) => {
        const { cart }: { cart: HblCart } = event

        if (cart == null) {
            return
        }

        clearEcommerce()
        pushEvent({
            event: 'view_cart',
            ecommerce: {
                currency: currencyCode, // required
                ...cartEcommerce(cart, affiliation)
            }
        })
    })

    $hblBus.$on('viewProductListing', (event) => {
        const { id, name, products }: { id: string, name: string, products: HblProduct[] } = event

        if (products == null) {
            return
        }

        clearEcommerce()
        pushEvent({
            event: 'view_item_list',
            ecommerce: {
                item_list_id: id,
                item_list_name: name,
                items: products.map((item, index) => {
                    return mapEcommerceItem(item, affiliation, { index, listId: id, listName: name })
                })
            }
        })
    })

    $hblBus.$on('clickProductListingCard', (event) => {
        const { id, name, product, index }: { id: string, name: string, product: HblProduct, index: number } = event

        if (product == null) {
            return
        }

        clearEcommerce()
        pushEvent({
            event: 'select_item',
            ecommerce: {
                item_list_id: id,
                item_list_name: name,
                items: [
                    mapEcommerceItem(product, affiliation, { index, listId: id, listName: name })
                ]
            }
        })
    })

    $hblBus.$on('viewProductDetail', (event) => {
        const { product }: { product: HblProduct } = event

        if (product == null) {
            return
        }

        const item = mapEcommerceItem(product, affiliation)

        clearEcommerce()
        pushEvent({
            event: 'view_item',
            ecommerce: {
                currency: currencyCode, // required
                value: sumItemsValue([item]), // required
                items: [item]
            }
        })
    })

    $hblBus.$on('viewCheckout', async () => {
        if (cart.value == null) {
            await getCart()
        }

        clearEcommerce()
        pushEvent({
            event: 'begin_checkout',
            ecommerce: {
                currency: currencyCode, // required
                ...cartEcommerce(cart.value, affiliation)
            }
        })
    })

    $hblBus.$on('selectShippingMethod', async (event) => {
        const { shipping }: { shipping: HblShippingMethod } = event

        if (shipping == null) {
            return
        }

        if (cart.value == null) {
            await getCart()
        }

        clearEcommerce()
        pushEvent({
            event: 'add_shipping_info',
            ecommerce: {
                currency: currencyCode, // required
                shipping_tier: shipping.name,
                ...cartEcommerce(cart.value, affiliation)
            }
        })
    })

    $hblBus.$on('selectPaymentMethod', async (event) => {
        const { payment }: { payment: HblPaymentMethod } = event

        if (payment == null) {
            return
        }

        if (cart.value == null) {
            await getCart()
        }

        clearEcommerce()
        pushEvent({
            event: 'add_payment_info',
            ecommerce: {
                currency: currencyCode, // required
                payment_type: payment.name,
                ...cartEcommerce(cart.value, affiliation)
            }
        })
    })

    $hblBus.$on('placeOrder', (event) => {
        const { order }: { order: HblOrder } = event

        if (order == null) {
            return
        }

        const items = mapEcommerceItems(order.lineItems, affiliation)
        const coupon = promotionCoupon(order.lineItems)

        clearEcommerce()
        pushEvent({
            event: 'purchase',
            ecommerce: {
                currency: currencyCode, // required
                value: sumItemsValue(items), // required
                transaction_id: order.orderNumber, // required
                shipping: orderShipping(order),
                tax: roundPrice(order.totals.bruttoPrice - order.totals.nettoPrice),
                ...(coupon != null && { coupon }),
                items
            }
        })
    })

    $hblBus.$on('login', () => {
        clearEcommerce()
        pushEvent({
            event: 'login'
        })
    })

    $hblBus.$on('register', () => {
        clearEcommerce()
        pushEvent({
            event: 'sign_up'
        })
    })
})
